import { memo, useCallback, useEffect, useRef, useState } from "react";

/**
 * Shared numeric input used across the whole app.
 *
 * Rules (matches Luke's test brief):
 *   1. Zero or empty renders as a BLANK box with a faint placeholder (never a
 *      typed "0" that the user has to delete first).
 *   2. Leading zeros are stripped on every keystroke and on blur. "045" → "45".
 *      "00.5" → "0.5". Negative sign is preserved. Empty / "-" / "." stays
 *      visually as-is until blur so the user can still type decimals/negatives.
 *   3. Focusing the box selects all text so the next keystroke replaces it.
 *   4. On blur, an empty or non-numeric value is normalised to blank in the UI
 *      but the value fired to the parent on blur is 0, so saves/totals keep
 *      working. (The parent's onBlur still receives the real event; we also
 *      fire a synthetic change with "" if the box was cleared.)
 *   5. Decimals & negatives: `step` controls precision; `allowNegative` lets
 *      a minus sign through for fields that need it (variations, adjustments).
 *
 * We keep this a drop-in for `<input className="input" type="number" ... />`
 * so each callsite only has to rename the tag — no changes to the surrounding
 * `onChange={e => ...}` handlers. The event we fire ALWAYS has a numeric-string
 * target.value with no leading zeros, so `parseFloat(e.target.value || 0)` keeps
 * returning the right number everywhere.
 */
function _strip(raw, allowNegative = true) {
  if (raw === "" || raw === null || raw === undefined) return "";
  let s = String(raw);
  // Keep only digits, one dot, optional leading minus.
  let neg = allowNegative && s.startsWith("-");
  s = s.replace(/[^0-9.]/g, "");
  // Only first dot survives
  const first = s.indexOf(".");
  if (first !== -1) s = s.slice(0, first + 1) + s.slice(first + 1).replace(/\./g, "");
  // Strip leading zeros, but keep "0." so decimals can be typed.
  if (s.length > 1 && s.startsWith("0") && !s.startsWith("0.")) {
    s = s.replace(/^0+/, "") || "0";
    if (s === "0") s = "0";  // "" case handled below
  }
  if (s === "") return neg ? "-" : "";
  return (neg ? "-" : "") + s;
}

function _displayFromExternal(v) {
  // Show nothing when the external value is 0 / "0" / "" / null / undefined.
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!isNaN(n) && n === 0) return "";
  return String(v);
}

function _fmtMoney(v) {
  if (v === "" || v === null || v === undefined) return "";
  const n = Number(v);
  if (isNaN(n) || n === 0) return "";
  return n.toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const NumberInput = memo(function NumberInput({
  value,
  onChange,
  onBlur,
  className = "input",
  placeholder = "0",
  step = "any",
  min,
  max,
  allowNegative = true,
  money = false,                 // when true: $ prefix label + comma-formatted display when unfocused
  "data-testid": testid,
  disabled,
  readOnly,
  title,
  ...rest
}) {
  const [local, setLocal] = useState(() => _displayFromExternal(value));
  const [focused, setFocused] = useState(false);
  const latest = useRef(value);
  const dirty = useRef(false);  // true while the user is editing

  useEffect(() => {
    if (!dirty.current && value !== latest.current) {
      setLocal(_displayFromExternal(value));
      latest.current = value;
    }
  }, [value]);

  const handleFocus = useCallback((e) => {
    setFocused(true);
    // money boxes swap "4,052.00" -> "4052" on focus, which collapses the
    // selection; re-select after that re-render so typing replaces the value.
    const el = e.target;
    try { el.select(); } catch { /* ignore */ }
    requestAnimationFrame(() => { try { if (document.activeElement === el) el.select(); } catch { /* ignore */ } });
  }, []);

  const handleChange = useCallback((e) => {
    dirty.current = true;
    const cleaned = _strip(e.target.value, allowNegative);
    setLocal(cleaned);
    const synthetic = { ...e, target: { ...e.target, value: cleaned } };
    onChange?.(synthetic);
  }, [onChange, allowNegative]);

  const handleBlur = useCallback((e) => {
    dirty.current = false;
    setFocused(false);
    let cleaned = _strip(local, allowNegative);
    if (cleaned === "-" || cleaned === ".") cleaned = "";
    const n = cleaned === "" ? 0 : Number(cleaned);
    const nextDisplay = (!cleaned || n === 0) ? "" : cleaned;
    setLocal(nextDisplay);
    latest.current = n;
    const synthetic = { ...e, target: { ...e.target, value: cleaned } };
    onBlur?.(synthetic);
  }, [local, onBlur, allowNegative]);

  // When money=true: show the comma-formatted amount while unfocused and the
  // raw cleaned number while editing. The $ prefix is drawn as an overlay so
  // it's visible in both states but doesn't get into the input value.
  const displayValue = money && !focused ? _fmtMoney(local) : local;

  const input = (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      className={className}
      value={displayValue}
      placeholder={placeholder}
      step={step}
      min={min}
      max={max}
      disabled={disabled}
      readOnly={readOnly}
      title={title}
      data-testid={testid}
      onFocus={handleFocus}
      onChange={handleChange}
      onBlur={handleBlur}
      style={money ? { paddingLeft: "1.1rem", ...(rest.style || {}) } : rest.style}
    />
  );
  if (!money) return input;
  return (
    <span className="relative inline-block w-full" data-testid={testid ? `${testid}-money-wrap` : undefined}>
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-silver pointer-events-none select-none" aria-hidden="true">$</span>
      {input}
    </span>
  );
});

export default NumberInput;
